import { useState } from 'react';
import { createTemplate, type OutreachTemplate, type OutreachCategory } from '@/lib/api/outreach';
import { useToast } from '@/lib/hooks/useToast';

interface Props {
  template: OutreachTemplate;      // pass a blank {id:''} to create
  categories: OutreachCategory[];
  onClose: () => void;
  onSaved: () => void;
}

export function TemplateEditModal({ template, categories, onClose, onSaved }: Props) {
  const { toast } = useToast();
  const [title, setTitle] = useState(template.title || '');
  const [category, setCategory] = useState(template.category || '');
  const [subject, setSubject] = useState(template.subject || '');
  const [body, setBody] = useState(template.body || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!title.trim()) { toast.error('Give the template a title'); return; }
    setSaving(true);
    try {
      await createTemplate({ id: template.id || undefined, title, category: category || undefined, subject, body });
      toast.success(template.id ? 'Template updated' : 'Template created');
      onSaved();
      onClose();
    } catch {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-lg font-bold text-gray-900">{template.id ? 'Edit template' : 'New template'}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-700">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <label className="flex flex-col gap-1">
              <span className="text-xs text-gray-500">Title</span>
              <input value={title} onChange={e => setTitle(e.target.value)} className="h-9 px-3 rounded-lg border border-gray-300 text-sm" />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-gray-500">Category</span>
              <select value={category} onChange={e => setCategory(e.target.value)} className="h-9 px-2 rounded-lg border border-gray-300 text-sm">
                <option value="">—</option>
                {categories.filter(c => c.id !== 'All').map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </label>
          </div>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-gray-500">Subject (email)</span>
            <input value={subject} onChange={e => setSubject(e.target.value)} className="h-9 px-3 rounded-lg border border-gray-300 text-sm" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-gray-500">Body — use [Your Name] and {'{{CREW}}'} / {'{{REFS}}'} / {'{{CLOSE}}'}</span>
            <textarea value={body} onChange={e => setBody(e.target.value)} rows={14}
              className="px-3 py-2 rounded-lg border border-gray-300 text-sm leading-relaxed" />
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button onClick={onClose} className="h-9 px-4 rounded-lg text-sm text-gray-600 hover:bg-gray-100">Cancel</button>
          <button onClick={save} disabled={saving} className="h-9 px-4 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90 disabled:opacity-60">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
